import { Smile, X } from '@/components/ui/icons';
import { useRef, useState, type FormEvent } from 'react';
import { updateProfileSchema } from '@clanfire/shared';
import { userApi } from '@/api/endpoints';
import { EmojiPicker } from '@/components/chat/EmojiPicker';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useSubmit, validate } from '@/hooks/useForm';
import { CLEAR_AFTER_OPTIONS, clearAfterToIso, describeExpiry, type ClearAfter } from '@/lib/customStatus';
import { useAuth } from '@/stores/auth';
import { toast } from '@/stores/ui';

export function CustomStatusModal({ onClose }: { onClose: () => void }) {
  const user = useAuth((s) => s.user)!;
  const [text, setText] = useState(user.customStatus ?? '');
  const [emoji, setEmoji] = useState(user.customStatusEmoji ?? '');
  const [clearAfter, setClearAfter] = useState<ClearAfter>(user.customStatusExpiresAt ? 'keep' : 'today');
  const [pickerOpen, setPickerOpen] = useState(false);
  const emojiButton = useRef<HTMLButtonElement>(null);
  const { loading, error, fields, setFields, run } = useSubmit();
  const existingExpiry = describeExpiry(user.customStatusExpiresAt);

  const save = async (payload: { customStatus: string | null; customStatusEmoji: string | null; customStatusExpiresAt: string | null }) => {
    const { data, fields: invalid } = validate(updateProfileSchema, payload);
    if (!data) return setFields(invalid);
    const updated = await run(() => userApi.updateProfile(data));
    if (!updated) return;
    useAuth.getState().setUser(updated);
    toast.success(updated.customStatus ? 'Status set.' : 'Status cleared.');
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed && !emoji) return void save({ customStatus: null, customStatusEmoji: null, customStatusExpiresAt: null });
    // An emoji alone is still a status; the server needs some text to hang it on.
    void save({ customStatus: trimmed || emoji, customStatusEmoji: trimmed ? emoji || null : null, customStatusExpiresAt: clearAfterToIso(clearAfter, user.customStatusExpiresAt) });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Set a custom status"
      size="sm"
      footer={
        <>
          {user.customStatus && (
            <Button variant="ghost" className="mr-auto" disabled={loading} onClick={() => void save({ customStatus: null, customStatusEmoji: null, customStatusExpiresAt: null })}>
              Clear status
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="custom-status-form" loading={loading}>
            Save
          </Button>
        </>
      }
    >
      <form id="custom-status-form" onSubmit={submit} className="flex flex-col gap-4">
        <div>
          <p className="mb-1.5 text-xs font-bold tracking-wide text-fg-soft uppercase">Status</p>
          <div className="flex items-start gap-2">
            <button
              ref={emojiButton}
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              aria-label={emoji ? `Status emoji ${emoji}, change` : 'Choose a status emoji'}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-line bg-sunken text-xl transition-colors hover:border-line-strong"
            >
              {emoji || <Smile size={20} className="text-fg-muted" />}
            </button>
            <div className="relative flex-1">
              <Input
                data-autofocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="What are you up to?"
                maxLength={128}
                aria-label="Status text"
                error={fields.customStatus}
                className="pr-9"
              />
              {(text || emoji) && (
                <button
                  type="button"
                  aria-label="Clear text and emoji"
                  onClick={() => {
                    setText('');
                    setEmoji('');
                  }}
                  className="absolute top-3 right-2.5 rounded-full p-0.5 text-fg-muted hover:text-fg"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
          <EmojiPicker open={pickerOpen} onClose={() => setPickerOpen(false)} anchor={emojiButton} onSelect={setEmoji} placement="bottom-start" allowCustom={false} />
        </div>
        <Select label="Clear after" value={clearAfter} onChange={(e) => setClearAfter(e.target.value as ClearAfter)} error={fields.customStatusExpiresAt}>
          {existingExpiry && <option value="keep">Keep current ({existingExpiry.replace('Clears ', '').replace(/^at /, '')})</option>}
          {CLEAR_AFTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}
